import { Link2 } from "lucide-react";
import { Type } from "@themes/pollinator/ui/Type";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { ClipboardCopyButton } from "@themes/pollinator/ui/ClipboardCopyButton";

export type Props = {
  shortcode: string;
  /** Custom title (default: "Share Your Link") */
  title?: string;
  /** Custom description — overrides the default max_uses-aware text */
  description?: string;
  /** Max number of times the invitation link can be used (undefined = unlimited) */
  maxUses?: number;
  /** Optional name label to display above the URL (e.g., program name) */
  name?: string;
  /** Whether to show the section heading wrapper (default: true) */
  showHeading?: boolean;
};

/**
 * Section displaying the direct invitation URL with copy button.
 */
function getDefaultDescription(maxUses?: number): string {
  const usesText =
    maxUses != null ? `${maxUses} friends` : "an unlimited number of friends";
  return `Instead of sending email invitations, you can use this link to invite ${usesText} directly. (Please share with care.)`;
}

export function InvitationLinkSection({
  shortcode,
  title = "Share Your Link",
  description,
  maxUses,
  name,
  showHeading = true,
}: Props) {
  const resolvedDescription = description ?? getDefaultDescription(maxUses);
  const invitationUrl = `${window.location.origin}/invitation/${shortcode}`;

  const content = (
    <div className="flex flex-col gap-2">
      {name && (
        <Type as="span" variant="h6">
          {name}
        </Type>
      )}
      <div className="flex flex-row gap-2 items-center">
        <div className="flex items-center gap-2 border bg-brand-50 rounded-sm py-2 px-4 flex-grow overflow-hidden">
          <Link2 className="w-4 h-4 text-brand-600 flex-shrink-0" />
          <Type as="span" variant="caption" className="text-brand-700 truncate">
            {invitationUrl}
          </Type>
        </div>
        <ClipboardCopyButton textToCopy={invitationUrl} label="Copy Link" />
      </div>
    </div>
  );

  if (!showHeading) {
    return content;
  }

  return (
    <SectionHeading
      title={title}
      description={resolvedDescription}
      className="gap-6"
    >
      {content}
    </SectionHeading>
  );
}

export default InvitationLinkSection;
